/**
 * Definition of all tool boxes for the various blockly workspaces.
 * Each workspace's toolbox is extracted by filtering on a specific 'subset'
 * On entries with 'addSeparator' the naming doesn't matter, but it helps to name it in some way
 * that helps identify under or above which element you want a line (separator) in the toolbox.
 */
module.exports = {
    page: {
        subSet: ['metadata:page'],
        color: ['#595959', '#595959'], //[block color, categoryColor]
        includeSecurityModifiers: true,
        allowedChildrenTypes: ['layout', 'documentation'],
        groupDescription: 'Navigation Units (pages and (sub) menus)',
        controls: ['Page', 'NavigationLink', 'NavigationSelectedContextPage'] //FKA NavHeading and TabNavHeading
    },
    navHeading: {
        subSet: ['metadata:navigation'],
        color: ['#595959', '#595959'], //[block color, categoryColor]
        includeSecurityModifiers: true,
        allowedChildrenTypes: ['layout', 'documentation'],
        groupDescription: 'Navigation Units (pages and (sub) menus)',
        controls: ['NavHeading', 'TabNavHeading']
    },
    menu: {
        subSet: ['metadata:page', 'metadata:menu'],
        color: ['#595959', '#595959'], //[block color, categoryColor]
        suppressTitle: true,
        includeSecurityModifiers: false,
        allowedChildrenTypes: ['menuSelection'],
        blockTypesAllowedToFollowThisOne: ['menu'],
        groupDescription: 'Menu Container',
        controls: ['MeatballProfileLayout', 'Menu']
    },
    menuSelection: {
        subSet: ['metadata:page', 'metadata:menu'],
        suppressTitle: true,
        includeSecurityModifiers: true,
        allowedChildrenTypes: ['menuSelection', 'actionElement', 'dataView'],
        blockTypesAllowedToFollowThisOne: ['menuSelection', 'dialog'],
        groupDescription: 'Menu items.',
        controls: ['MenuDivider', 'MenuHeader', 'MenuItem', 'MenuShell', 'SubMenu']
    },
    accessModifier: {
        suppressTitle: true,
        color: ['#000', '#000'], //[block color, categoryColor]
        //It does have connectors, but those are done manually, as it is slightly different:
        includeSecurityModifiers: false,
        blockTypesAllowedToFollowThisOne: [],
        groupDescription: 'Access Modification to control conditional access to specific areas.',
        controls: [
            'AddForFeatureFlag',
            'CustomizableVisibility',
            'DisableWhen',
            'HiddenFor',
            'HiddenForValue',
            'HideOn',
            'HiddenWhen',
            'ReadOnlyFor',
            'RemoveForFeatureFlag',
            'HideForTenantSetting',
            'ShowForTenantSetting',
            'ShowForViewPort'
        ]
    },
    displayModifier: {
        suppressTitle: true,
        includeSecurityModifiers: false,
        blockTypesAllowedToFollowThisOne: [],
        groupDescription: 'Display modification to control visual presentation of values and rows.',
        controls: ['RowHighlight', 'PillBox']
    },
    actionElement: {
        subSet: ['metadata:page', 'metadata:menu'],
        includeSecurityModifiers: true,
        // Need formButton in here in case we put an actionElement in the button area of a form.
        blockTypesAllowedToFollowThisOne: [
            'actionElement',
            'layout',
            'dataView',
            'dialog',
            'formElement',
            'formButton'
        ],
        groupDescription: 'Data entry or action (i.e. button) control which is not embedded in a form.',
        controls: [
            'Button',
            'DateRangePicker',
            'DownloadButton',
            'FindRecord',
            'Href',
            'ImportForm',
            'InfoDisplayButton',
            'KeySearch',
            'MarkRecord',
            'MultiPickerTransactionGrid',
            'MenuButton',
            'PowerSliderPopup',
            'Range',
            'SensorReadButton',
            'Slider',
            'Toggle'
        ]
    },

    afterActionElementSeparator: {
        subSet: ['metadata:page', 'metadata:menu'],
        addSeparator: true
    },
    blocklyView: {
        subSet: ['metadata:page'],
        includeSecurityModifiers: false,
        blockTypesAllowedToFollowThisOne: ['layout'],
        groupDescription: 'Used to define application metadata using Blockly blocks.',
        controls: [
            'BlocklyEditor',
            'DataDictionaryViewer',
            'MenuEditor',
            'MetaDataViewer',
            'SwaggerDocViewer',
            'ValidationViewer'
        ]
    },
    dataView: {
        subSet: ['metadata:page', 'metadata:menu'],
        includeSecurityModifiers: true,
        // blockTypesAllowedToFollowThisOne: ['actionElement', 'layout', 'dataView', 'dialog'],
        blockTypesAllowedToFollowThisOne: null, // explicit set to null to allow any block to follow this type
        groupDescription: 'Provide a readonly view of some data.',
        controls: [
            'GeigerCounter',
            'ImageView',
            'JsonViewer',
            'FixedText',
            'PrintPreview',
            'ShortTextView',
            'DataAggregation'
        ]
    },
    detailPane: {
        subSet: ['metadata:page', 'metadata:menu'],
        suppressTitle: true,
        includeSecurityModifiers: true,
        allowedChildrenTypes: ['layout', 'form', 'dataView'],
        blockTypesAllowedToFollowThisOne: ['detailPane'],
        groupDescription:
            'Usually attached to a "New" button or a grid.  Specifies the layout of the item which will be changed.',
        controls: ['DetailPane']
    },
    dialog: {
        subSet: ['metadata:page', 'metadata:menu'],
        includeSecurityModifiers: true,
        allowedChildrenTypes: ['layout', 'list', 'form', 'dataView', 'actionElement', 'graph'],
        blockTypesAllowedToFollowThisOne: ['dialog', 'actionElement'],
        groupDescription:
            'Usually attached to a "New" button or a grid.  Specifies the layout of the item which will be changed.',
        controls: ['SimpleDialog']
    },
    documentation: {
        subSet: ['metadata:page'],
        includeSecurityModifiers: false,
        blockTypesAllowedToFollowThisOne: [],
        groupDescription: 'Provide a view of documentation',
        controls: ['Swagger']
    },
    filter: {
        subSet: ['metadata:page', 'metadata:menu'],
        suppressTitle: true,
        includeSecurityModifiers: false,
        blockTypesAllowedToFollowThisOne: ['filter'],
        groupDescription: 'Used to filter data returned by a repository.',
        controls: [
            'AlsoSearchWithAscii',
            'AggregateType',
            'ByCurrentRoute',
            'ByCurrentUser',
            'ForeignRelationActiveRecord', //By Grid Selection
            'ByNavigationSelection',
            'ByNavigationSelectionDropdownCriteria',
            'ByNavigationSelectionCriteria',
            'ForeignRelationSelectionOnForm', //By Parent Selection
            'ForeignRelationSplashRecord', // By Splash Screen Selection
            'DateRange',
            'DefaultToPreviousSelection',
            'DefaultValue',
            'ExcludedValues',
            'ExactMatchOnly',
            'GroupBy',
            'GroupByBoolean',
            'IncludeDeleted',
            'IncludedValues',
            'NestedListEntry',
            'OrderBy',
            'OrUndefined',
            'Paged',
            'PropertiesToSearch',
            'ValueDoesNotExist',
            'ValueExists'
        ]
    },
    beforeFormSeparator: {
        subSet: ['metadata:page', 'metadata:menu'],
        addSeparator: true
    },
    form: {
        subSet: ['metadata:page', 'metadata:menu'],
        includeSecurityModifiers: false,
        allowedChildrenTypes: ['formElement', 'workflowPane', 'dataView', 'layout', 'dialog', 'actionElement'],
        blockTypesAllowedToFollowThisOne: [],
        groupDescription: 'A form for data entry or transactions.',
        controls: ['Form', 'SubForm', 'SingleRecordForm', 'Transaction']
    },
    formButton: {
        subSet: ['metadata:page', 'metadata:menu'],
        includeSecurityModifiers: true,
        blockTypesAllowedToFollowThisOne: ['formButton'],
        allowedChildrenTypes: ['workflowLogic'],
        groupDescription: 'A button to perform an action on a form such as submitting the form or cancelling.',
        controls: ['FormButton', 'MenuButton']
    },
    formElement: {
        subSet: ['metadata:page', 'metadata:menu'],
        includeSecurityModifiers: true,
        blockTypesAllowedToFollowThisOne: ['formElement', 'workflowPane', 'layout', 'dialog', 'actionElement'],
        groupDescription: 'A data entry control for a form.',
        controls: [
            'Coordinates',
            'Currency',
            'DatePicker',
            'DeferredDynamicFieldEntry',
            'DropDown',
            'DropDownWithFilter',
            'DynamicFieldEntry',
            'EditableList',
            'ElementGroups',
            'Email',
            'FilePicker',
            'Image',
            'Integer',
            'InvisibleElement',
            'JsonViewer',
            'LongText',
            'MultiPickerDropDown',
            'MultiPickerSensorGrid',
            'MultiPickerShortText',
            'NestedObject',
            'PageAccess',
            'Password',
            'PasswordWithConfirm',
            'ReadOnlyDateTime',
            'ReadOnlyFormList',
            'ReadOnlyText',
            'ShortEncodedText',
            'ShortText',
            'Slider',
            'TextLabel',
            'Toggle'
        ]
    },
    transform: {
        subSet: ['metadata:page', 'metadata:menu'],
        suppressTitle: true,
        includeSecurityModifiers: false,
        blockTypesAllowedToFollowThisOne: ['transform'],
        groupDescription: 'Used to transform a value',
        controls: [
            'ToUpperCase',
            'ToLowerCase',
            'ToNumber',
            'ToBoolean',
            'ToHexFromAscii',
            'ToAsciiFromHex',
            'DefaultToPreviousSelection',
            'TabAfterScan',
            'ActionAfterScan'
        ]
    },
    afterTransformSeparator: {
        subSet: ['metadata:page', 'metadata:menu'],
        addSeparator: true
    },
    graph: {
        subSet: ['metadata:page'],
        includeSecurityModifiers: true,
        allowedChildrenTypes: ['actionElement'],
        blockTypesAllowedToFollowThisOne: ['graph', 'layout'],
        groupDescription: 'A graph or other reporting widget.',
        controls: ['HorizontalChart', 'PieChart', 'TotalItemsElement', 'VerticalChart']
    },
    layout: {
        subSet: ['metadata:page', 'metadata:menu'],
        includeSecurityModifiers: true,
        allowedChildrenTypes: [
            'layout',
            'list',
            'form',
            'dataView',
            'dialog',
            'actionElement',
            'graph',
            'detailPane',
            'documentation',
            'blocklyView',
            'workflowPane',
            'searchPane'
        ],
        blockTypesAllowedToFollowThisOne: ['layout', 'graph', 'formElement', 'dialog'],
        groupDescription: 'A container to organize controls.',
        controls: [
            'DashboardLayout',
            'DetailHSplitLayout', //Not implemented in UI2.0 yet
            'DetailVSplitLayout', // Deprecated
            'EvenColumnsLayout',
            'FilterBoundaryLayout',
            'FormElementLayout',
            'GreedyColumnLayout',
            'GreedySectionLayout',
            'LeftAlignLayout',
            // 'MillerColumns',
            'MosaicLayout',
            'RightAlignLayout',
            'SpaceApartLayout',
            'SummaryDetailLayout',
            'TabViewLayout'
        ]
    },
    beforeListSeparator: {
        subSet: ['metadata:page'],
        addSeparator: true
    },
    list: {
        subSet: ['metadata:page'],
        suppressTitle: true,
        includeSecurityModifiers: true,
        blockTypesAllowedToFollowThisOne: ['layout'],
        allowedChildrenTypes: ['filter', 'listColumn'],
        groupDescription: 'Used to display multiple documents for selection or reporting.',
        controls: ['CardList', 'TableList', 'SensorReadList']
    },
    listColumn: {
        subSet: ['metadata:page'],
        includeSecurityModifiers: true,
        blockTypesAllowedToFollowThisOne: ['listColumn'],
        groupDescription: 'Designate what columns or card elements will display in a list.',
        controls: [
            'Barcode',
            'Calculated',
            'Currency',
            'DateTime',
            'EmbeddedList',
            'EncodedText',
            'ForeignRelationText',
            // Renders a "scanned / expected" string as a progress bar (#1909). The value is
            // maintained server-side as plain text, so a Text column shows the same numbers;
            // this only adds the bar.
            'Fraction',
            'GpsCoordinates',
            'LineBreak',
            'Link',
            'LocalVerboseDateTime',
            'MappedBoolean',
            'NestedDetail',
            'PatchDetail',
            'Text',
            'Thumbnail'
        ]
    },
    beforeSearchSeparator: {
        subSet: ['metadata:page'],
        addSeparator: true
    },
    searchPane: {
        subSet: ['metadata:page'],
        includeSecurityModifiers: true,
        allowedChildrenTypes: ['searchElement'],
        blockTypesAllowedToFollowThisOne: [],
        groupDescription: 'Search containers to hold search elements',
        controls: ['SearchPane']
    },
    searchElement: {
        subSet: ['metadata:page'],
        includeSecurityModifiers: true,
        blockTypesAllowedToFollowThisOne: ['searchElement'],
        groupDescription: 'Used to build your filter/search criteria.',
        controls: [
            'ValueSearch',
            'DropDownSearch',
            'MultiPickerDropDownSearch',
            'MultiUserPickerDropDownSearch',
            'Range',
            'NestedDetail',
            'ToggleSearch',
            'IncludedValueSearch'
        ]
    },
    rangeType: {
        subSet: ['metadata:page', 'metadata:menu'],
        includeSecurityModifiers: true,
        blockTypesAllowedToFollowThisOne: [],
        groupDescription: 'Types of values to use with the Range Action Element',
        controls: ['DateTime', 'Number', 'ShortText', 'Integer']
    },
    beforeWorkflowSeparator: {
        subSet: ['metadata:page'],
        addSeparator: true
    },
    workflowPane: {
        subSet: ['metadata:page'],
        includeSecurityModifiers: true,
        blockTypesAllowedToFollowThisOne: ['formElement', 'workflowPane', 'layout'],
        groupDescription: 'Workflow definition containers',
        controls: ['WorkflowCriteriaPane', 'WorkflowActionList', 'WorkflowActionPane']
    },
    workflowCriteriaElement: {
        subSet: ['metadata:page'],
        includeSecurityModifiers: true,
        blockTypesAllowedToFollowThisOne: ['workflowCriteriaElement'],
        groupDescription: 'Data entry control for workflow criteria ',
        controls: ['ShortTextCriteria', 'IntegerCriteria', 'DropDownCriteria', 'MultiPickerDropDownCriteria']
    },
    workflowLogic: {
        subSet: ['metadata:page', 'metadata:menu'],
        suppressTitle: true,
        includeSecurityModifiers: false,
        blockTypesAllowedToFollowThisOne: ['workflowLogic'],
        groupDescription: 'logic that can be executed on the client before or after e.g. a button click',
        controls: ['showConfirmationMessage']
    }
};
